/* eslint-disable no-undef */
import Head from 'next/head';
import PropTypes from 'prop-types';

import Layout from 'components/layout';
import { availableSorts, DEFAULT_SORT } from 'utils/filters';
import { getSeoRelLinks } from 'components/paginator';
import { baseTranslations } from 'utils/translations';
import { getGridProps, getProducts } from 'actions/products';
import { getSeo } from 'actions/general';
import { filterTransformer, DEFAULT_LIMIT, getRealID, COOKIES } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { TIER_POSITIONS } from 'utils/constants/cms';
import { getDomainValue } from 'utils/domain';

import { ProductsListPage } from 'components/grid/product-list-page';

function WithLayout({ defaultProps, children, breadcrumbI18n, camperlab, recycled, isApp, breadcrumbsBottom, breadcrumbs }) {
  if (isApp) {
    return children;
  }

  return (
    <Layout
      {...defaultProps}
      breadcrumbI18n={breadcrumbI18n}
      breadcrumbs={breadcrumbs}
      breadcrumbsBottom={breadcrumbsBottom}
      scrollArrow={true}
      camperlab={camperlab}
      recycled={recycled}
    >
      {children}
    </Layout>
  );
}

WithLayout.propTypes = {
  children: PropTypes.node,
  isApp: PropTypes.bool,
  footerProps: PropTypes.any,
  tnmProps: PropTypes.any,
  breadcrumbI18n: PropTypes.object,
  ribbon: PropTypes.any,
  camperlab: PropTypes.bool,
  recycled: PropTypes.bool,
  tiersProps: PropTypes.object,
  title: PropTypes.string,
  breadcrumbsBottom: PropTypes.bool,
};

export default function ProductsListBrand({ defaultProps, hrefLangLinks, breadcrumbI18n, gtmProps, camperlab, metatags, recycled, isApp, ...rest }) {
  const hasStandalone = rest.tiersProps[TIER_POSITIONS.STANDALONE] !== null && rest.tiersProps[TIER_POSITIONS.STANDALONE] !== undefined;
  const hasTitleDescription = rest.title !== null && rest.title !== undefined && rest.description !== null && rest.description !== undefined;
  const isGridSeeAll = rest.filtersCount === 0 && !rest.hasFiltersUrl;
  const seoLinks = getSeoRelLinks(rest.initialPage, rest.totalPages, rest.urlCanonical, isGridSeeAll);
  return (
    <WithLayout
      defaultProps={{
        ...defaultProps,
        metatags,
        pageName: 'grid',
        extratags: [seoLinks],
        gtmProps: { ...gtmProps, products: rest.firstProducts },
      }}
      isApp={isApp}
      breadcrumbs={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
      breadcrumbI18n={getDomainValue({ camper: breadcrumbI18n, nnormal: breadcrumbI18n, camperlab: {} })}
      breadcrumbsBottom={hasStandalone || hasTitleDescription}
      camperlab={camperlab}
      recycled={recycled}
      scrollArrow={true}
    >
      <Head>
        {hrefLangLinks?.map((l) => (
          <link key={`hreflang-${l.hrefLang}`} rel="alternate" hrefLang={l.hrefLang} href={l.url} />
        ))}
      </Head>
      <ProductsListPage {...rest} isApp={isApp} gtmProps={gtmProps} camperlab={camperlab} breadcrumbI18n={breadcrumbI18n} />
    </WithLayout>
  );
}

// ProductsListBrand.propTypes = {
//   firstProducts: PropTypes.array,
//   total: PropTypes.number,
//   availableFilters: PropTypes.object,
//   baseAvailable: PropTypes.object,
//   locale: PropTypes.string,
//   defaultLocale: PropTypes.string,
//   datetime: PropTypes.string,
//   uuid: PropTypes.string,
//   appliedFilters: PropTypes.object,
//   filtersCount: PropTypes.number,
//   hasFiltersInURL: PropTypes.bool,
//   footerProps: PropTypes.any,
//   tnmProps: PropTypes.any,
//   gtmProps: PropTypes.object,
//   tiersProps: PropTypes.object,
//   initialPage: PropTypes.number,
//   currentList: PropTypes.string,
//   ribbon: PropTypes.any,
//   camperlab: PropTypes.bool,
//   title: PropTypes.string,
//   description: PropTypes.string,
//   metatags: PropTypes.object,
//   breadcrumbI18n: PropTypes.object,
//   autoappliedVoucher: PropTypes.bool,
//   urlCanonical: PropTypes.string,
//   originalCta: PropTypes.object,
//   resolvedUrl: PropTypes.string,
//   seoContent: PropTypes.object,
//   recycled: PropTypes.bool,
//   subgridProducts: PropTypes.array,
//   subgridUrlCollection: PropTypes.string,
// };

ProductsListBrand.i18nPage = 'grid';

// import type from next GetServerSidePropsContext
/** @param {import("next").GetServerSidePropsContext} context  */
export async function getServerSideProps(context) {
  const lang = context.locale;
  const { target, product, brand, page, sort, ...rest } = context.query;
  const targets = Object.keys(baseTranslations.target)
    .map((langCode) => baseTranslations.target[langCode])
    .reduce((a, b) => a.concat(b));
  const families = Object.keys(baseTranslations.product)
    .map((langCode) => baseTranslations.product[langCode])
    .reduce((a, b) => a.concat(b));

  if (!targets.includes(target) || !families.includes(product)) {
    return {
      notFound: true,
    };
  }

  const filters = {};

  Object.entries(rest).forEach(([key, value]) => {
    if (['page', 'sort'].includes(key)) {
      filters[key] = value;
    }
    if (key.startsWith('filter.')) {
      filters[key] = value;
    }
  });

  const hasFiltersInURL = Object.keys(filters).length > 0;

  try {
    const targetRealID = getRealID('target', target);
    const familyRealID = getRealID('product', product);

    const pageNumber = Number(page);
    const pageOffset = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const appliedSort = availableSorts.includes(sort) && sort !== DEFAULT_SORT ? sort : false;

    let gridId = null;
    let hrefLangLinks = [];
    try {
      const gridProps = await getGridProps({
        context,
        locale: lang,
        slug: brand,
        cache: context.query?.cache ?? undefined,
        targetId: targetRealID,
        familyId: familyRealID,
      });
      if (Array.isArray(gridProps.grid.filters)) {
        gridProps.grid.filters.forEach(({ type, value }) => {
          filters[`filter.${type}`] = value;
        });
      }
      if (gridProps.grid.id) {
        gridId = gridProps.grid.id;
      }
      if (Array.isArray(gridProps.grid.hrefLangs)) {
        hrefLangLinks = gridProps.grid.hrefLangs;
      }
    } catch (error) {
      console.error('Error retrieving gridProps', JSON.stringify(error));
    }

    const previousPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset - DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
        });
    const nextPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset + DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
        });

    // la segunda llamada es para traer los filtros de la pagina. Porque si limitamos con filters no trae todos, solo los que aplica a los productos respondidos
    const [
      {
        products,
        total,
        availableFilters,
        currentList,
        currentListId,
        camperlab,
        title,
        description,
        urlCanonical,
        urlCanonicalGTM,
        seoContent,
        rewalk,
        relatedCollectionId,
        urlRelatedCollection,
      },
      filtersResponse,
      previousPage,
      nextPage,
    ] = await Promise.all([
      getProducts({
        context,
        lang,
        target,
        page: pageOffset,
        gridId,
        filters,
        sort: appliedSort,
        product,
        seoContent: true,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
      }),
      getProducts({
        context,
        lang,
        target,
        product,
        page: 0,
        limit: 1,
        filters: null,
      }),
      previousPagePromise,
      nextPagePromise,
    ]);

    const { defaultProps, tiers, cmsSeoContent } = await initCMS({
      context,
      locale: lang,
      tierParams: ['grid', [''], targetRealID, familyRealID, gridId],
      tnmSelectedTarget: targetRealID,
      seoContent: camperlab ? null : { target: targetRealID },
      camperlab,
    });

    const baseAvailable = filtersResponse.availableFilters;
    const appliedFilters = Object.keys(filters).length > 0 ? filterTransformer.decode(filters, availableFilters) : {};
    const filtersCount = Object.keys(appliedFilters).length > 0 ? Object.keys(appliedFilters).reduce((a, b) => appliedFilters[b].length + a, 0) : 0;

    const gtmProps = {
      target,
      targetRealID,
      product,
      familyRealID,
      currentList: currentList ?? null,
      currentListId: currentListId ?? null,
      camperlab,
      currentListName: title ?? '',
      page: pageNumber - 1,
      url: urlCanonicalGTM ?? null,
    };

    const queryParams = [
      `profile=${lang}`,
      `family=${familyRealID || '1'}`,
      `gender=${targetRealID || 'X'}`,
      `option=${targetRealID || 'X'}`,
      'page=PRODUCTOS',
    ];
    const collectionFilter = filters['filter.collection'];
    const conceptFilter = filters['filter.concept'];
    const typologyFilter = filters['filter.typology'];
    const sustainabilityFilter = filters['filter.sustainability'];
    if (collectionFilter) {
      queryParams.push(`specialCollection=${collectionFilter}`);
    }
    if (conceptFilter) {
      queryParams.push(`collection=${conceptFilter}`);
    }
    if (typologyFilter) {
      queryParams.push(`typology=${typologyFilter}`);
    }
    if (sustainabilityFilter) {
      queryParams.push(`sustainability=${sustainabilityFilter}`);
    }
    const hasSubgrid = relatedCollectionId !== null && relatedCollectionId !== undefined && relatedCollectionId !== '';
    let subgridUrlCollection = '';
    let subgridProducts = [];
    if (hasSubgrid) {
      const resolvedUrlWithoutQuery = context.resolvedUrl.split('?')[0];
      subgridUrlCollection = urlRelatedCollection || `/${lang}${resolvedUrlWithoutQuery}?filter.collection=${relatedCollectionId}`;
      const subgridResponse = await getProducts({
        context,
        lang,
        target,
        page: 0,
        limit: 8,
        filters: { 'filter.collection': relatedCollectionId },
        sort: false,
        product,
        seoContent: false,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
      });
      subgridProducts = subgridResponse.products.slice(0, 8);
    }

    // Con el nuevo cambio de tbl_grid hay que pasar el gridId a getSeo
    if (gridId) {
      queryParams.push(`gridId=${gridId}`);
    }
    const metatags = await getSeo(context, queryParams);

    const breadcrumbI18n = {
      target: targetRealID.toLocaleLowerCase() || null,
      family: familyRealID || null,
      brand: title || null,
    };

    const originalCta = {};
    if (tiers?.standalone && tiers?.standalone?.ctaText) {
      originalCta.text = tiers.standalone.ctaText;
      if (tiers.standalone.ctaTextProps) {
        originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor || tiers?.textProps?.fontColor || '#000000';
        tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
      }
    }
    const isApp = context?.req?.cookies[COOKIES.APP_IPAD] === 'true' || false;

    return {
      props: {
        defaultProps,
        hrefLangLinks,
        currentList: currentList || null,
        defaultSort: availableSorts.includes(sort) ? sort : DEFAULT_SORT,
        firstProducts: products,
        previousPageProducts: previousPage?.products ?? null,
        nextPageProducts: nextPage?.products?.length > 0 ? nextPage.products : null,
        total,
        availableFilters,
        baseAvailable,
        appliedFilters,
        filtersCount,
        hasFiltersInURL,
        gtmProps,
        tierParams: ['grid', [''], targetRealID, familyRealID, gridId],
        tiersProps: !isApp && tiers ? tiers : {},
        initialPage: pageNumber || 1,
        camperlab,
        title: title ?? null,
        description: description ?? null,
        metatags,
        breadcrumbI18n,
        urlCanonical,
        originalCta,
        resolvedUrl: context.resolvedUrl,
        recycled: rewalk ?? false,
        seoContent:
          seoContent ?
            {
              ...seoContent,
              cms:
                cmsSeoContent ?
                  {
                    ...cmsSeoContent,
                    gridName: currentList ?? title ?? '',
                  }
                : null,
            }
          : null,
        subgridProducts,
        subgridUrlCollection,
        isApp,
      },
    };
  } catch (e) {
    console.error(e);
    context.res.statusCode = 500;
    return {
      props: {
        error: `${context.resolvedUrl} SSR Error: ${e}`,
        permanent: false,
      },
    };
  }
}
